import api from '@/services/api';
import type {
    Agreement,
    AgreementCashPosition,
    AgreementInvoice,
    AgreementInvoiceLine,
    FundingPurpose,
    InvoiceFundingType,
    DealerCollectedPayment,
    ElemisApplication,
    InvoicingCandidate,
    SoldAddOn,
    SoldItem,
    SoldLines,
    SoldService,
    SubmitInvoicePayload,
    InvoiceLinePayload,
    PendingFundingApproval,
    OwnDealerLiveAgreement,
    VehicleAgreementCheck,
    VehicleAgreementCheckQuery,
    InvoicingRoute,
} from '../types/agreements.types';
import { scaledMagnitudeOf } from '../utils/invoiceSelection';
import { readString } from '../utils/snapshotReaders';
import {
    readArray,
    readInteger,
    readMoney,
    readNullableInteger,
    readObject,
    readOptionalBoolean,
    readOptionalMoney,
    readQuantity,
} from './wireReaders';
import { mapCancelAction, mapCancellationRecord } from './cancellation';
import { toMajorNumber } from '@/app/utils/toMajorNumber';

// C1 — GATEWAY TRAILING SLASH. `infrastructure/nginx/docker/default.conf` registers
// `location /api/agreements/` (trailing slash = prefix match) and the server block has NO
// catch-all `location /`. CORRECTION (#714 gate G1b, verified 2026-08-06): a request without
// the slash 301-redirects to the canonical form with the query string intact rather than
// 404ing, so the cost of getting this wrong is a round-trip, not a break. Send the slash
// anyway — it avoids the redirect, and a 301 downgrades a non-GET to GET if the collection
// ever grows a POST. This is the estate convention, not a workaround — see
// www/src/features/customers/services/customers.ts and
// www/src/features/dashboard/services/dashboard.ts, both of which build the collection URL
// with a trailing slash.
const path = '/agreements';
const collectionPath = `${path}/`; // ALWAYS use this for collection requests.

const asString = (value: unknown): string | null => readString(value) ?? null;

// Read via the `dealerPayments` relation on `GET agreements/{id}` — no new route.
const mapDealerPayment =(raw: Record<string, unknown>): DealerCollectedPayment => ({
    id: String(raw.id),
    kind: String(raw.kind ?? ''),
    amount: asString(raw.amount),
    reference: asString(raw.reference),
    invoiceNumber: asString(raw.invoice_number),
    collectedAt: asString(raw.collected_at),
});

const mapDealerPayments = (raw: unknown): DealerCollectedPayment[] =>
    Array.isArray(raw) ? raw.map((entry) => mapDealerPayment(entry as Record<string, unknown>)) : [];

const toInvoicingRoute = (value: unknown): InvoicingRoute | undefined =>
    value === 'open' || value === 'closed' ? value : undefined;

const mapAgreement = (raw: Record<string, unknown>): Agreement => ({
    id: String(raw.id),
    agreementRef: String(raw.agreement_ref ?? ''),
    status: String(raw.status ?? ''),
    invoicingRoute: toInvoicingRoute(raw.invoicing_route),
    cancellation: mapCancellationRecord(raw.cancellation),
    cancelAction: mapCancelAction(raw.actions),
    startDate: asString(raw.start_date),
    quoteId: String(raw.quote_id ?? ''),
    customerId: String(raw.customer_id ?? ''),
    vehicleId: asString(raw.vehicle_id),
    dealerId: String(raw.dealer_id ?? ''),
    campaignId: asString(raw.campaign_id),
    productId: asString(raw.product_id),
    planId: asString(raw.plan_id),
    elemisAgreementRef: asString(raw.elemis_agreement_ref),
    vin: asString(raw.vin),
    totalCost: toMajorNumber(raw.total_cost),
    totalGross: toMajorNumber(raw.total_gross),
    feeNet: toMajorNumber(raw.fee_net),
    feeGross: toMajorNumber(raw.fee_gross),
    createdAt: asString(raw.created_at),
    updatedAt: asString(raw.updated_at),
    // Verbatim by design (two-tier rule). Defaulted so accessors never see undefined.
    customerSnapshot: (raw.customer_snapshot ?? {}) as Record<string, unknown>,
    vehicleSnapshot: (raw.vehicle_snapshot ?? {}) as Record<string, unknown>,
    purchaseSnapshot: (raw.purchase_snapshot ?? {}) as Record<string, unknown>,
    dealerPayments: mapDealerPayments(raw.dealer_payments),
});

export const fetchAgreement = async (
    id: string,
    options: { suppressGlobalForbiddenToast?: boolean } = {},
): Promise<Agreement> => {
    const response = options.suppressGlobalForbiddenToast
        ? await api.get(`${path}/${id}`, { suppressGlobalForbiddenToast: true })
        : await api.get(`${path}/${id}`);
    return mapAgreement(response.data.data);
};

/** F3: the dashboard holds a QUOTE id. quote_id is unique on agreements, so at
 *  most one row comes back. `null` (not a throw) when none exists.
 *
 *  `null` has TWO causes and the caller must not claim to know which (M1):
 *    a) no Agreement exists for that quote — every plan row predating
 *       agreements-service, and any quote whose consumer has not run yet;
 *    b) an Agreement exists but belongs to a dealer outside the caller's
 *       entitlement — the sibling plan's dealer-scoping ANDs a dealer predicate
 *       onto this query, so an out-of-scope hit also returns `{ data: [] }`.
 *  Both are a legitimate 200. Neither is an error. See NoAgreementForQuote.
 *
 *  Uses `collectionPath` (trailing slash) — C1. */
export const fetchAgreementByQuote = async (
    quoteId: string,
    options: { suppressGlobalForbiddenToast?: boolean } = {},
): Promise<Agreement | null> => {
    const params = { quote_id: quoteId, per_page: 1 };
    const raw = (
        options.suppressGlobalForbiddenToast
            ? await api.get(collectionPath, { params, suppressGlobalForbiddenToast: true })
            : await api.get(collectionPath, { params })
    ).data;
    const first = ((raw.data ?? []) as Record<string, unknown>[])[0];
    return first ? mapAgreement(first) : null;
};

/** D3 — the only mutation in this feature. `reason` is an optional operator
 *  note persisted onto the history row by the state machine; omitted from the
 *  body entirely when not supplied (the backend's ActivateAgreementRequest
 * treats it as nullable). On success returns the now-active Agreement;
 *  on an illegal transition (already active, or a terminal state — #96) the
 *  shared Axios interceptor rejects with an ApiError whose `.message` is the
 *  backend's verbatim sentence (e.g. "Agreement ... is active: transition
 *  \"activate\" is not permitted from that state.") and whose `.data` carries
 *  the enveloped `{ error: { type, code: 'illegal_transition', message },
 *  agreement_id, current_state, attempted, allowed, success }` shape (#948) — see
 *  AgreementDraftNotice for how the 422 is surfaced. `ApiError.message` is
 *  byte-identical to before #948; `ApiError.code` is now populated with
 *  `'illegal_transition'` rather than `undefined`. */
export const activateAgreement = async (id: string, reason?: string): Promise<Agreement> =>
    mapAgreement((await api.patch(`${path}/${id}/activate`, reason ? { reason } : {})).data.data);

export interface InitiateElemisApplicationOptions {
    retry?: boolean;
    sendViaEmail?: boolean;
    sendViaSms?: boolean;
    expectedVersion?: number;
}

/** Body is `{ quote_id, send_via_email, send_via_sms }`, plus `retry` only after a terminal failure
 *  and `expected_version` when the caller knows the quote version;
 *  everything that touches money is server-side. Never add a request timeout. */
export const initiateElemisApplication = async (
    quoteId: string,
    options: InitiateElemisApplicationOptions = {},
): Promise<ElemisApplication> => {
    const body = {
        quote_id: quoteId,
        ...(options.retry ? { retry: true } : {}),
        ...(typeof options.expectedVersion === 'number' ? { expected_version: options.expectedVersion } : {}),
        send_via_email: options.sendViaEmail ?? false,
        send_via_sms: options.sendViaSms ?? false,
    };
    const raw = (await api.post(`${path}/elemis-applications`, body)).data.data;
    return {
        applicationId: String(raw.application_id),
        checkoutUrl: String(raw.checkout_url),
        agreementId: String(raw.agreement_id),
        agreementRef: String(raw.agreement_ref ?? ''),
        replayed: Boolean(raw.replayed),
    };
};

const mapSoldItem = (raw: Record<string, unknown>): SoldItem => ({
    id: String(raw.id),
    name: String(raw.name ?? ''),
    description: asString(raw.description),
    code: asString(raw.code),
    quantity: readQuantity(raw.quantity),
    unitNet: readMoney(raw.unit_net),
    unitGross: readMoney(raw.unit_gross),
    ...readOptionalMoney(raw, 'line_net', 'lineNet'),
    ...readOptionalMoney(raw, 'line_gross', 'lineGross'),
    placeholder: readOptionalBoolean(raw.placeholder, 'placeholder'),
    status: String(raw.status ?? ''),
});

const mapSoldService = (raw: Record<string, unknown>): SoldService => ({
    id: String(raw.id),
    sequence: readInteger(raw.sequence, 'sequence'),
    name: String(raw.name ?? ''),
    serviceType: asString(raw.service_type),
    dueDate: asString(raw.due_date),
    dueMileage: readNullableInteger(raw.due_mileage, 'due mileage'),
    pricedAt: String(raw.priced_at ?? ''),
    net: readMoney(raw.net),
    gross: readMoney(raw.gross),
    items: readArray(raw.items, 'items', 'sold-line').map(mapSoldItem),
});

const mapSoldAddOn = (raw: Record<string, unknown>): SoldAddOn => ({
    id: String(raw.id),
    name: String(raw.name ?? ''),
    description: asString(raw.description),
    quantity: readQuantity(raw.quantity),
    unitNet: readMoney(raw.unit_net),
    unitGross: readMoney(raw.unit_gross),
    linkedServiceId: asString(raw.linked_service_id),
    dueDate: asString(raw.due_date),
    status: String(raw.status ?? ''),
});

const MAX_SCALED_TOTAL = BigInt(Number.MAX_SAFE_INTEGER);

/** P-D7 sums a selection in ten-thousandths during render. Bounding the select-everything
 *  worst case here keeps every subtotal an exact integer without a throw in the render path. */
const assertSummableInTenThousandths = (lines: SoldLines): void => {
    if (scaledMagnitudeOf(lines) > MAX_SCALED_TOTAL) {
        throw new Error('Sold-line totals exceed the exact ten-thousandths range');
    }
};

/** Order is the server's: no client re-sort (the sequence and the created-at tiebreak
 *  are the contract). */
export const fetchAgreementSoldItems = async (id: string): Promise<SoldLines> => {
    const raw = readObject((await api.get(`${path}/${id}/sold-items`)).data.data);
    const lines: SoldLines = {
        services: readArray(raw.services, 'services', 'sold-line').map(mapSoldService),
        addOns: readArray(raw.add_ons, 'add_ons', 'sold-line').map(mapSoldAddOn),
    };
    assertSummableInTenThousandths(lines);
    return lines;
};

const BALANCE_MONEY_PATTERN = /^-?\d+(\.\d{1,4})?$/;

/** The balance gates a financial action, so an unusable figure must never become a
 *  comparison that silently passes. Mirrors invoicing-service's HttpAgreementReader. */
export const fetchAgreementCashPosition = async (id: string): Promise<AgreementCashPosition> => {
    const raw = readObject((await api.get(`${path}/${id}/cash-position`)).data.data);
    const position = raw.cash_position;

    if (typeof position !== 'string' || !BALANCE_MONEY_PATTERN.test(position)) {
        throw new Error('The agreement cash position response did not include a usable cash_position.');
    }

    const parsed = Number(position);

    // A long digit string parses to Infinity, which would compare as an
    // unlimited balance. Reject anything the 4dp scaled comparison cannot hold.
    if (!Number.isFinite(parsed) || Math.abs(parsed) > Number.MAX_SAFE_INTEGER / 10_000) {
        throw new Error('The agreement cash position response had an out-of-range cash_position.');
    }
    if (raw.currency !== 'GBP') {
        throw new Error('The agreement cash position response used an unsupported currency.');
    }

    return { cashPosition: parsed };
};

const mapCandidate = (raw: Record<string, unknown>): InvoicingCandidate => ({
    id: String(raw.id),
    name: String(raw.name ?? ''),
});

const mapInvoiceLine = (raw: Record<string, unknown>): AgreementInvoiceLine => ({
    id: String(raw.id),
    kind: String(raw.kind ?? ''),
    name: String(raw.name ?? ''),
    quantity: readQuantity(raw.quantity),
    net: toMajorNumber(raw.net),
    gross: toMajorNumber(raw.gross),
    agreementItemIds: Array.isArray(raw.agreement_item_ids) ? raw.agreement_item_ids.map(String) : [],
});

const FUNDING_TYPES: readonly InvoiceFundingType[] = ['balance', 'arrears', 'pot_total'];

const readFundingType = (value: unknown): InvoiceFundingType | null =>
    FUNDING_TYPES.find((type) => type === value) ?? null;

export const mapInvoice = (raw: Record<string, unknown>): AgreementInvoice => ({
    id: String(raw.id),
    agreementId: String(raw.agreement_id ?? ''),
    invoiceNumber: String(raw.invoice_number ?? ''),
    invoicedAt: String(raw.invoiced_at ?? ''),
    invoicingDealerId: String(raw.invoicing_dealer_id ?? ''),
    mileage: typeof raw.mileage === 'number' ? raw.mileage : null,
    status: String(raw.status ?? ''),
    totalNet: toMajorNumber(raw.total_net),
    totalGross: toMajorNumber(raw.total_gross),
    submittedBy: String(raw.submitted_by ?? ''),
    submittedAt: String(raw.submitted_at ?? ''),
    fundingType: readFundingType(raw.funding_type),
    claimedGross: toMajorNumber(raw.claimed_gross),
    shortfallGross: toMajorNumber(raw.shortfall_gross),
    fundingApprovalId: asString(raw.funding_approval_id),
    reversedAt: asString(raw.reversed_at) ?? undefined,
    reversedBy: asString(raw.reversed_by) ?? undefined,
    reversedByName: asString(raw.reversed_by_name) ?? undefined,
    reversalReason: asString(raw.reversal_reason) ?? undefined,
    lines: Array.isArray(raw.lines) ? (raw.lines as Record<string, unknown>[]).map(mapInvoiceLine) : [],
});

/** Populates the invoicing-dealer picker only; the POST re-resolves candidates
 *  server-side, so the two lists can legitimately diverge. */
export const fetchInvoicingCandidates = async (
    dealerId: string,
    productId: string | null,
): Promise<InvoicingCandidate[]> => {
    const raw = (
        await api.get(`/security/dealers/${dealerId}/invoicing-candidates`, {
            params: productId ? { product_id: productId } : {},
        })
    ).data.data;
    return readArray(raw, 'candidates', 'invoicing-candidate').map(mapCandidate);
};

/** Newest first is the server's order; not re-sorted here. */
export const fetchAgreementInvoices = async (agreementId: string): Promise<AgreementInvoice[]> => {
    const raw = (await api.get(`/invoicing/agreements/${agreementId}/invoices`)).data.data;
    return readArray(raw, 'invoices', 'invoice').map(mapInvoice);
};

export const requestFundingApproval = async (
    agreementId: string,
    purpose: FundingPurpose,
    lines: InvoiceLinePayload[],
    approverEmail: string,
): Promise<PendingFundingApproval> => {
    const raw = readObject(
        (
            await api.post(`/invoicing/agreements/${agreementId}/funding-approvals`, {
                purpose,
                lines,
                approver_email: approverEmail,
            })
        ).data.data,
    );
    return {
        approvalId: String(raw.approval_id ?? ''),
        approverName: String(raw.approver_name ?? ''),
        approverEmail,
        purpose,
        shortfallGross: readMoney(raw.shortfall_gross),
        expiresAt: String(raw.expires_at ?? ''),
    };
};

export const submitInvoice = async (
    agreementId: string,
    payload: SubmitInvoicePayload,
): Promise<AgreementInvoice> => {
    const raw = (await api.post(`/invoicing/agreements/${agreementId}/invoices`, payload)).data.data;
    return mapInvoice(raw as Record<string, unknown>);
};


const mapOwnDealerAgreement = (raw: Record<string, unknown>): OwnDealerLiveAgreement => ({
    id: String(raw.id),
    agreementRef: String(raw.agreement_ref ?? ''),
    status: String(raw.status ?? '') as OwnDealerLiveAgreement['status'],
});

export const checkVehicleAgreements = async (
    query: VehicleAgreementCheckQuery,
): Promise<VehicleAgreementCheck> => {
    const params: VehicleAgreementCheckQuery = {};
    if (query.vin) params.vin = query.vin;
    if (query.vrm) params.vrm = query.vrm;

    const response = await api.get(`${path}/vehicle-check`, {
        params,
        suppressGlobalForbiddenToast: true,
    });
    const data = (response.data?.data ?? {}) as Record<string, unknown>;
    const ownDealer = Array.isArray(data.own_dealer)
        ? data.own_dealer.map((entry) => mapOwnDealerAgreement(entry as Record<string, unknown>))
        : [];

    return {
        hasLiveAgreement: data.has_live_agreement === true,
        ownDealer,
        otherDealer: data.other_dealer === true,
    };
};
